/**
 * V37.59 — Widget "Mes services LEGALCOOP".
 *
 * Affiche dans les dashboards Membre/Cooper :
 *  - statut de la demande
 *  - facture telechargeable (si paid/exempted/in_review/completed)
 *  - lien vers le projet CCMI concerne
 *  - etat paiement / assignation / revue terminee
 *
 * V37.76 — Choix de paiement (Tache 3 e1) : deux boutons cote a cote
 *  - "Payer par carte (Stripe)"  -> Stripe Checkout
 *  - "Payer avec mes crédits"    -> debit credits (solde affiche)
 *
 * Le backend filtre automatiquement par requester_email du JWT (voir
 * legalcoop_list_requests : role 'membre'/'cooper'/etc -> ses propres demandes).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, Scale, FileDown, CreditCard, ExternalLink, Coins } from 'lucide-react';
import { LEGALCOOP_OFFICIAL_DEFINITION } from '../constants/legalcoop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_payment: { label: 'En attente paiement', color: 'bg-amber-600 text-white border-amber-800 font-semibold' },
  paid: { label: 'Payé', color: 'bg-blue-700 text-white border-blue-900 font-semibold' },
  exempted: { label: 'Exonéré', color: 'bg-purple-700 text-white border-purple-900 font-semibold' },
  assigned: { label: 'Assigné juriste', color: 'bg-cyan-700 text-white border-cyan-900 font-semibold' },
  in_review: { label: 'En revue juridique', color: 'bg-indigo-700 text-white border-indigo-900 font-semibold' },
  completed: { label: 'Revue terminée', color: 'bg-emerald-700 text-white border-emerald-900 font-semibold' },
  cancelled: { label: 'Annulée', color: 'bg-red-700 text-white border-red-900 font-semibold' },
};

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
        || localStorage.getItem('access_token')
        || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function MesServicesLegalcoop() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyInvoice, setBusyInvoice] = useState(null);
  const [busyPay, setBusyPay] = useState(null);
  const [busyPayCredits, setBusyPayCredits] = useState(null);
  // V37.76 — options de paiement par service_type ({service_type: options})
  const [payOptions, setPayOptions] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/legalcoop/requests?limit=20`, { headers: getAuthHeader() });
      const list = res.data?.items || [];
      setItems(list);
      // V37.76 — charge les options credits pour chaque service en attente
      const types = [...new Set(
        list.filter((i) => i.status === 'pending_payment')
            .map((i) => i.service_type || 'compliance_review'),
      )];
      const opts = {};
      await Promise.all(types.map(async (t) => {
        try {
          const r = await axios.get(
            `${API}/legalcoop/payment-options?service_type=${t}`,
            { headers: getAuthHeader() },
          );
          opts[t] = r.data;
        } catch { /* bouton credits non affiche */ }
      }));
      setPayOptions(opts);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => { if (mounted) await load(); })();
    return () => { mounted = false; };
  }, [load]);

  const downloadInvoice = async (item) => {
    setBusyInvoice(item.id);
    try {
      const res = await axios.get(
        `${API}/legalcoop/requests/${item.id}/facture-pdf`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.headers?.['x-invoice-number'] || 'facture-legalcoop'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Facture téléchargée');
    } catch (err) {
      let detail = err?.response?.data?.detail;
      if (!detail && err?.response?.data instanceof Blob) {
        try { const t = await err.response.data.text(); detail = JSON.parse(t)?.detail; } catch { /* noop */ }
      }
      toast.error(detail || 'Erreur téléchargement facture');
    } finally {
      setBusyInvoice(null);
    }
  };

  const openStripeCheckout = async (item) => {
    setBusyPay(item.id);
    try {
      const res = await axios.post(
        `${API}/legalcoop/requests/${item.id}/stripe-checkout`,
        { origin_url: window.location.origin },
        { headers: getAuthHeader() },
      );
      const url = res.data?.checkout_url;
      if (url) { window.open(url, '_blank'); toast.success('Lien Stripe ouvert'); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur paiement Stripe');
    } finally {
      setBusyPay(null);
    }
  };

  // V37.76 — Paiement avec les credits du compte.
  const payWithCredits = async (item) => {
    setBusyPayCredits(item.id);
    try {
      const res = await axios.post(
        `${API}/legalcoop/requests/${item.id}/pay-with-credits`,
        {},
        { headers: getAuthHeader() },
      );
      if (res.data?.role_exempt) {
        toast.success('Service exonéré (règle de rôle) — aucun crédit débité');
      } else {
        toast.success(`Payé avec ${res.data?.credits_spent} crédits — nouveau solde : ${res.data?.balance_after}`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur paiement par crédits');
    } finally {
      setBusyPayCredits(null);
    }
  };

  if (loading) {
    return (
      <Card data-testid="mes-services-legalcoop-loading">
        <CardContent className="py-6 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement de vos services LEGALCOOP...
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null; // silencieux : pas un blocage du dashboard
  }

  if (!items.length) {
    return null; // n'affiche rien si le membre n'a aucune demande
  }

  return (
    <Card data-testid="mes-services-legalcoop">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Scale className="h-5 w-5 text-indigo-700" />
          Mes services LEGALCOOP
        </CardTitle>
        <p className="text-xs text-slate-500">Forfait Revue de conformité CCMI — 500 € HT par dossier</p>
        <p className="text-[11px] text-slate-600 mt-2 italic leading-relaxed" data-testid="mes-services-legalcoop-definition">
          {LEGALCOOP_OFFICIAL_DEFINITION}
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((it) => {
            const meta = STATUS_LABEL[it.status] || STATUS_LABEL.pending_payment;
            const canDownloadInvoice = ['paid', 'exempted', 'in_review', 'assigned', 'completed'].includes(it.status);
            const canPay = it.status === 'pending_payment';
            const opt = payOptions[it.service_type || 'compliance_review'];
            const creditsOk = !!opt?.credits_enabled;
            const sufficient = !!opt?.credits_sufficient;
            return (
              <div
                key={it.id}
                className="border border-slate-200 rounded p-3 hover:bg-slate-50 transition-colors"
                data-testid={`mes-services-legalcoop-item-${it.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-[10px] ${meta.color}`} data-testid={`mes-services-status-${it.id}`}>
                        {meta.label}
                      </Badge>
                      <span className="font-mono text-xs text-slate-700">
                        CCMI {it.ccmi_project_reference || '-'}
                      </span>
                      <span className="text-[10px] text-slate-400">{it.id.slice(0, 8)}</span>
                      {it.payment_provider === 'credits' && (
                        <Badge className="text-[10px] bg-amber-700 text-white border-amber-900 font-semibold"
                          data-testid={`mes-services-paid-credits-${it.id}`}>
                          Payé par crédits ({it.credits_spent})
                        </Badge>
                      )}
                      {it.invoice_number && (
                        <span className="text-[10px] text-emerald-700 font-semibold">
                          {it.invoice_number}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">
                      {it.amount_ht} € HT
                      {it.assigned_juriste && <> • Juriste <b>{it.assigned_juriste}</b></>}
                      {it.paid_at && <> • Payé le {it.paid_at.slice(0, 10)}</>}
                      {it.completed_at && <> • Terminé le {it.completed_at.slice(0, 10)}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {canPay && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs bg-blue-50 border-blue-200 text-blue-700"
                          onClick={() => openStripeCheckout(it)}
                          disabled={busyPay === it.id || busyPayCredits === it.id}
                          data-testid={`mes-services-pay-${it.id}`}
                        >
                          {busyPay === it.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CreditCard className="h-3 w-3 mr-1" />}
                          Payer par carte (Stripe)
                        </Button>
                        {creditsOk && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                            onClick={() => payWithCredits(it)}
                            disabled={busyPay === it.id || busyPayCredits === it.id || !sufficient}
                            title={!sufficient ? `Solde insuffisant (${opt?.credit_balance ?? 0} crédits)` : ''}
                            data-testid={`mes-services-pay-credits-${it.id}`}
                          >
                            {busyPayCredits === it.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Coins className="h-3 w-3 mr-1" />}
                            Payer avec mes crédits ({opt?.credit_balance ?? 0})
                          </Button>
                        )}
                      </>
                    )}
                    {canDownloadInvoice && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs bg-slate-50 border-slate-200 text-slate-700"
                        onClick={() => downloadInvoice(it)}
                        disabled={busyInvoice === it.id}
                        data-testid={`mes-services-invoice-${it.id}`}
                      >
                        {busyInvoice === it.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileDown className="h-3 w-3 mr-1" />}
                        Facture
                      </Button>
                    )}
                    {it.ccmi_project_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => { window.location.href = `/admin/ccmi-dashboard?project=${it.ccmi_project_id}`; }}
                        data-testid={`mes-services-ccmi-${it.id}`}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" /> CCMI
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-400 italic mt-3">
          Revue documentaire d&apos;aide à la conformité — ne constitue pas un avis juridique définitif.
        </p>
      </CardContent>
    </Card>
  );
}
